<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

const slides = [
  {
    id: 1,
    img: '/slides/slide1.png',
    label: 'Smart Ordering',
    title: 'Place orders in seconds',
    desc: 'Pin your pick-up and drop-off on the live map, choose your vehicle size, and dispatch — all from one screen.',
  },
  {
    id: 2,
    img: '/slides/slide2.png',
    label: 'Live Tracking',
    title: 'Watch every delivery unfold',
    desc: 'Real-time route tracking with driver details, stop-by-stop progress, and a full activity timeline.',
  },
  {
    id: 3,
    img: '/slides/slide3.png',
    label: 'Full Order History',
    title: 'Your complete delivery record',
    desc: 'Every order, status, cost and route - expandable, searchable and exportable to CSV in one click.',
  },
];

const currentSlide = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;
const goTo = (i: number) => { currentSlide.value = i; resetTimer(); };
const nextSlide = () => { currentSlide.value = (currentSlide.value + 1) % slides.length; };
const resetTimer = () => { if (timer) clearInterval(timer); timer = setInterval(nextSlide, 4800); };
onMounted(() => resetTimer());
onUnmounted(() => { if (timer) clearInterval(timer); });
</script>

<template>
  <div class="as-panel">
    <div class="as-grid"></div>
    <div class="as-overlay"></div>

    <!-- Brand -->
    <div class="as-brand">
      <img src="/logo.png" alt="XOBO logo" class="as-brand-logo" />
    </div>

    <!-- Slides -->
    <div class="as-body">
      <TransitionGroup name="slide-fade" tag="div" class="as-slides-wrap">
        <div v-for="(slide, i) in slides" :key="slide.id" v-show="currentSlide === i" class="as-slide">
          <div class="as-mockup">
            <div class="as-browser-bar">
              <span class="as-dot as-dot--red"></span>
              <span class="as-dot as-dot--amber"></span>
              <span class="as-dot as-dot--green"></span>
              <span class="as-url-bar">biz.xobo.co.ke</span>
            </div>
            <img :src="slide.img" class="as-screenshot" :alt="slide.title" />
            <div class="as-screenshot-fade"></div>
          </div>
          <div class="as-caption">
            <span class="as-caption-label">{{ slide.label }}</span>
            <h2 class="as-caption-title">{{ slide.title }}</h2>
            <p class="as-caption-desc">{{ slide.desc }}</p>
          </div>
        </div>
      </TransitionGroup>
    </div>

    <!-- Dots -->
    <div class="as-dots">
      <button
        v-for="(_, i) in slides"
        :key="i"
        class="as-dot-btn"
        :class="{ active: currentSlide === i }"
        @click="goTo(i)"
      ></button>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');

.as-panel {
  position: relative;
  width: 58%;
  flex-shrink: 0;
  background: #0c1f3a;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 100vh;
}

.as-grid {
  position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 52px 52px;
}

.as-overlay {
  position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background: linear-gradient(160deg, rgba(18,40,74,0.55) 0%, rgba(8,18,36,0.7) 100%);
}

/* Brand */
.as-brand {
  position: relative; z-index: 2;
  display: flex; align-items: center; gap: 10px;
  padding: 28px 52px;
}

.as-brand-logo {
  height: 40px;
  width: auto;
  object-fit: contain;
  display: block;
}

/* Slide body */
.as-body {
  position: relative; z-index: 2;
  flex: 1; display: flex; align-items: center; justify-content: center;
  padding: 0 52px 20px; overflow: hidden;
}

.as-slides-wrap {
  position: relative; width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
}

.as-slide {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; justify-content: center; gap: 24px;
}

/* Mockup */
.as-mockup {
  position: relative; border-radius: 12px; overflow: hidden; flex-shrink: 0;
  background: #1a2744;
  box-shadow: 0 32px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.08), 0 4px 16px rgba(0,0,0,0.4);
}

.as-browser-bar {
  display: flex; align-items: center; gap: 6px;
  background: #1e2d4d; padding: 9px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}

.as-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.as-dot--red   { background: #ff5f57; }
.as-dot--amber { background: #febc2e; }
.as-dot--green { background: #28c840; }

.as-url-bar {
  margin-left: 10px; border-radius: 5px;
  background: rgba(255,255,255,0.07); padding: 3px 12px;
  font-size: 0.72rem; color: rgba(255,255,255,0.4);
  font-family: 'DM Sans', sans-serif; letter-spacing: 0.02em;
}

.as-screenshot {
  display: block; width: 100%; height: auto;
  /*max-height: 420px; */ 
  object-fit: cover; object-position: top;
}

.as-screenshot-fade {
  position: absolute; bottom: 0; left: 0; right: 0; height: 100px;
  background: linear-gradient(to bottom, transparent, rgba(12,31,58,0.85));
  pointer-events: none;
}

/* Caption */
.as-caption { flex-shrink: 0; }

.as-caption-label {
  display: inline-block; margin-bottom: 10px;
  font-size: 0.68rem; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; color: #22c55e;
  background: rgba(34,197,94,0.12); border: 1px solid rgba(34,197,94,0.25);
  border-radius: 20px; padding: 3px 10px;
}

.as-caption-title {
  font-family: 'DM Sans', sans-serif; font-size: 1.55rem; font-weight: 700;
  color: white; margin: 0 0 8px; line-height: 1.25; letter-spacing: -0.02em;
}

.as-caption-desc {
  font-size: 0.84rem; font-weight: 300; color: rgba(255,255,255,0.55);
  margin: 0; line-height: 1.65; max-width: 440px;
}

/* Dots */
.as-dots {
  position: relative; z-index: 2;
  display: flex; gap: 8px; justify-content: center;
  padding: 0 52px 36px;
}

.as-dot-btn {
  width: 24px; height: 4px; border-radius: 2px; border: none;
  background: rgba(255,255,255,0.2); cursor: pointer; padding: 0;
  transition: background 0.3s, width 0.35s cubic-bezier(0.4,0,0.2,1);
}
.as-dot-btn.active { width: 44px; background: #22c55e; }

/* Transitions */
.slide-fade-enter-active { transition: opacity 0.6s ease, transform 0.6s cubic-bezier(0.4,0,0.2,1); }
.slide-fade-leave-active { transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.4,0,0.2,1); position: absolute; }
.slide-fade-enter-from { opacity: 0; transform: translateX(40px); }
.slide-fade-leave-to  { opacity: 0; transform: translateX(-30px); }

/* Hide on mobile */
@media (max-width: 900px) { .as-panel { display: none; } }
</style>